import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Video, Camera, Share2, Star, Film, Calendar, User, MapPin, Settings } from "lucide-react";
import logo from "@/assets/logo-white.png";
import MarketingHeader from "@/components/marketing/MarketingHeader";

// Import images
import festivalVideoCrew from "@/assets/user-cases/festival-video-crew.jpg";
import photographyTeam from "@/assets/user-cases/photography-team.jpg";
import socialMediaTeams from "@/assets/user-cases/social-media-teams.jpg";
import artistOneTakers from "@/assets/user-cases/artist-one-takers.jpg";
import dailyRecap from "@/assets/user-cases/daily-recap.jpg";
import multiDayFestival from "@/assets/user-cases/multi-day-festival.jpg";
import freelancers from "@/assets/user-cases/freelancers.jpg";
import liveTracking from "@/assets/user-cases/live-tracking.jpg";
import producerAccess from "@/assets/user-cases/producer-access.jpg";

interface UseCaseProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  image?: string;
  gradient: string;
}

const useCases: UseCaseProps[] = [
  {
    icon: <Video className="h-6 w-6" />,
    title: "Festival video crew coordination",
    description:
      "Manage dozens of camera operators across multiple stages. Assign shooters to time blocks, prevent overlaps, and track who is filming where in real time.",
    features: [
      "Personal planning per crew member",
      "Real-time location tracking",
      "Instant push notifications for changes",
      "Clear briefing exports (PDF + print friendly)",
    ],
    image: festivalVideoCrew,
    gradient: "from-primary to-secondary",
  },
  {
    icon: <Camera className="h-6 w-6" />,
    title: "Photography team management",
    description:
      "Let photographers focus on shooting while PROduce handles timing, locations, and switching between stages.",
    features: [
      "Assign artists or custom subjects",
      '"Next up" tracking on the lock screen',
      "ETA warnings if someone risks running late",
      "Easy day-overview for the producer",
    ],
    image: photographyTeam,
    gradient: "from-secondary to-primary",
  },
  {
    icon: <Share2 className="h-6 w-6" />,
    title: "Social media content teams",
    description:
      "Content creators often move rapidly across the festival. PROduce keeps them aligned with the overall strategy.",
    features: [
      "Flexible assignment blocks",
      "Real-time changes visible instantly",
      "Crew tracking on the live map",
      "Quick rescheduling with AI prompts",
    ],
    image: socialMediaTeams,
    gradient: "from-primary to-secondary",
  },
  {
    icon: <Star className="h-6 w-6" />,
    title: "Artist one-takers",
    description: "Perfect for behind-the-scenes content or high-priority artist moments.",
    features: [
      "Custom subject creation",
      "Live updates when an artist moves or delays",
      "Personal mini-planning for each specialist",
      "Easy communication between producer and crew",
    ],
    image: artistOneTakers,
    gradient: "from-secondary to-primary",
  },
  {
    icon: <Film className="h-6 w-6" />,
    title: "Daily recap operations",
    description: "Coordinate shooters, runners, ingest teams, and editors in one unified system.",
    features: [
      "Producer portal with a full real-time overview",
      "Break planning",
      "Assign multiple crew members to overlapping tasks",
      "Live ETA calculations between stages",
    ],
    image: dailyRecap,
    gradient: "from-primary to-secondary",
  },
  {
    icon: <Calendar className="h-6 w-6" />,
    title: "Multi-day festival management",
    description: "Built for events like Mysteryland, Defqon.1, Tomorrowland, etc.",
    features: [
      "Per-day planning",
      "Stage zones mapped on the tracking map",
      "Full producer control dashboard",
      "Archived productions for future reference",
    ],
    image: multiDayFestival,
    gradient: "from-secondary to-primary",
  },
  {
    icon: <User className="h-6 w-6" />,
    title: "Freelancers managing their own festival day",
    description:
      "Freelance photographers and videographers can create their own planning—even without a full production team.",
    features: [
      "Free crew member account",
      "Personal schedule builder",
      "Custom subjects",
      "AI scheduling assistance",
      'Automatic "Next up" lockscreen widget',
    ],
    image: freelancers,
    gradient: "from-primary to-secondary",
  },
  {
    icon: <MapPin className="h-6 w-6" />,
    title: "Track crew members' live locations",
    description: "Producers can see where every crew member is on the festival terrain.",
    features: [
      "Live GPS tracking (only inside festival boundaries)",
      "ETA to next assignment",
      "Red urgency state when someone may be late",
      "Map with stage zones drawn by the producer",
      "Real-time updates throughout the day",
    ],
    image: liveTracking,
    gradient: "from-secondary to-primary",
  },
  {
    icon: <Settings className="h-6 w-6" />,
    title: "Producers always have full access",
    description:
      "Even during showtime, producers can modify plans, reassign crew, and push updates instantly.",
    features: [
      "Open the producer portal at any moment",
      "Modify planning (time, crew, location, subject)",
      "Instantly push changes to the entire team",
      "Add new assignments on the spot",
      "Fix time clashes instantly",
    ],
    image: producerAccess,
    gradient: "from-primary to-secondary",
  },
];

const UseCaseCard = ({ icon, title, description, features, image, gradient }: UseCaseProps) => (
  <Card className="group overflow-hidden border-border bg-card/50 backdrop-blur-xl transition-all hover:border-primary/50">
    {image && (
      <div className="aspect-video overflow-hidden">
        <img
          src={image}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
    )}
    <div className="p-6">
      <div
        className={`h-12 w-12 rounded-xl bg-gradient-to-br ${gradient} mb-4 flex items-center justify-center`}
      >
        <div className="text-white">{icon}</div>
      </div>
      <h3 className="mb-2 text-xl font-bold">{title}</h3>
      <p className="mb-4 text-muted-foreground">{description}</p>
      <div className="space-y-2">
        <p className="text-sm font-semibold text-primary">Relevant features:</p>
        <ul className="space-y-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-1 text-primary">•</span>
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </Card>
);

const UserCases = () => {
  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader />

      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="mb-6 text-4xl font-bold md:text-5xl">
              Built for every
              <span className="text-gradient-primary"> production scenario</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Discover how festival producers, video crews, photographers, and freelancers use
              PROduce to streamline their productions.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase, index) => (
              <UseCaseCard key={index} {...useCase} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: "#050e22" }}>
        <div className="container mx-auto px-6 text-center">
          <h2 className="mb-4 text-3xl font-bold">Ready to streamline your production?</h2>
          <p className="mx-auto mb-8 max-w-xl text-muted-foreground">
            Start planning your next festival production in minutes with PROduce.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/producer-login">
              <button className="rounded-lg bg-gradient-to-r from-primary to-secondary px-8 py-3 font-semibold text-white transition-opacity hover:opacity-90">
                Start free trial
              </button>
            </Link>
            <Link to="/contact">
              <button className="rounded-lg border border-border bg-card px-8 py-3 font-semibold text-foreground transition-colors hover:bg-card/80">
                Contact us
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12" style={{ backgroundColor: "#050e22" }}>
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <img src={logo} alt="PROduce Logo" className="h-8" />
            <div className="flex items-center gap-6">
              <Link
                to="/user-cases"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Use Cases
              </Link>
              <Link
                to="/privacy-policy"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/contact"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Contact
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 PROduce. Elevating festival production worldwide.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserCases;
